import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { salesOrdersAPI } from '../../services/api';
import InvoiceCreationModal from '../../components/InvoiceCreationModal';
import { OrderNeedingInvoice, getOrdersNeedingInvoice } from '../../services/quickbooksService';
import './OrderDetail.css';

interface OrderItem {
  id: number;
  formula_name: string;
  is_pellet: boolean;
  total_quantity_lbs: number;
  number_of_packages: number;
  package_type?: string;
  unit_price: number;
  total_price: number;
  price_per_package?: number | null;
  pricing_display_method?: string;
  is_promo?: boolean;
  promo_reason?: string;
}

interface EditLog {
  id: number;
  edited_by_name: string;
  edited_at: string;
  reason: string;
  changes: Record<string, { old: any; new: any }>;
}

interface Order {
  id: number;
  order_number: string;
  customer_name: string;
  customer_id?: number;
  customer_email?: string;
  customer_address?: string;
  customer_city?: string;
  customer_state?: string;
  customer_zip?: string;
  order_date: string;
  delivery_date: string;
  status: string;
  shipping_method: string;
  is_rush: boolean;
  is_tax_exempt: boolean;
  liftgate_required?: boolean;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  special_instructions: string;
  items: OrderItem[];
  entered_by_name?: string;
  invoice_id?: number;
  invoice_number?: string;
  invoice_status?: string;
  edit_logs?: EditLog[];
  tracking_number?: string | null;
  carrier?: string | null;
  shipped_date?: string | null;
}

// Status pipeline for the stepper
const STATUS_PIPELINE = [
  { key: 'PENDING', label: 'Pending' },
  { key: 'CONFIRMED', label: 'Confirmed' },
  { key: 'IN_PRODUCTION', label: 'In Production' },
  { key: 'MILLED', label: 'Milled' },
  { key: 'PACKAGING', label: 'Packaging' },
  { key: 'READY_TO_SHIP', label: 'Ready to Ship' },
  { key: 'SHIPPED', label: 'Shipped' },
  { key: 'DELIVERED', label: 'Delivered' },
];

const CARRIER_OPTIONS = [
  'Fastway Freight',
  'Peninsula Truck Lines',
  'Red Bridge Delivery (Milk Run)',
  'Other',
];

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [orderForInvoice, setOrderForInvoice] = useState<OrderNeedingInvoice | null>(null);

  // Status update modal state
  const [statusModal, setStatusModal] = useState<{
    open: boolean;
    targetStatus: string;
  }>({ open: false, targetStatus: '' });
  const [statusNote, setStatusNote] = useState('');
  const [statusSendEmail, setStatusSendEmail] = useState(true);
  const [statusTrackingNumber, setStatusTrackingNumber] = useState('');
  const [statusCarrier, setStatusCarrier] = useState('');
  const [statusCarrierCustom, setStatusCarrierCustom] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const response = await salesOrdersAPI.getById(parseInt(id));
      setOrder(response.data);
    } catch (err: any) {
      console.error('Error loading order:', err);
      setError(err.response?.data?.error || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadgeClass = (status: string) => {
    const statusMap: Record<string, string> = {
      PENDING: 'status-pending',
      CONFIRMED: 'status-confirmed',
      IN_PRODUCTION: 'status-production',
      PRODUCTION: 'status-production',
      MILLED: 'status-production',
      PACKAGING: 'status-production',
      READY: 'status-ready',
      READY_TO_SHIP: 'status-ready',
      SHIPPED: 'status-shipped',
      DELIVERED: 'status-delivered',
      CANCELLED: 'status-cancelled',
      DRAFT: 'status-draft',
    };
    return statusMap[status] || 'status-default';
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatItemPricing = (item: OrderItem) => {
    const isBaggedOrder = item.pricing_display_method === 'PER_PACKAGE' ||
      ((!item.pricing_display_method || item.pricing_display_method === 'PER_LB') ? false :
        (item.package_type === 'BAGS' || item.package_type === 'BAGS_19'));

    if (isBaggedOrder && item.number_of_packages > 0) {
      const pricePerBag = item.price_per_package || (item.total_price / item.number_of_packages);
      return `${formatCurrency(pricePerBag)}/bag × ${item.number_of_packages} bags = ${formatCurrency(item.total_price)}`;
    }

    const pricePerLb = item.total_quantity_lbs > 0 ? item.total_price / item.total_quantity_lbs : 0;
    return `$${pricePerLb.toFixed(4)}/lb × ${item.total_quantity_lbs.toLocaleString()} lbs = ${formatCurrency(item.total_price)}`;
  };

  // Status stepper helpers
  const getCurrentStepIndex = () => {
    if (!order) return -1;
    return STATUS_PIPELINE.findIndex(s => s.key === order.status);
  };

  const openStatusModal = (targetStatus: string) => {
    setStatusModal({ open: true, targetStatus });
    setStatusNote('');
    setStatusSendEmail(true);
    setStatusTrackingNumber('');
    setStatusCarrier('');
    setStatusCarrierCustom('');
  };

  const handleUpdateStatus = async () => {
    if (!order) return;
    const { targetStatus } = statusModal;

    try {
      setUpdatingStatus(true);
      const carrier = statusCarrier === 'Other' ? statusCarrierCustom : statusCarrier;
      await salesOrdersAPI.updateStatus(order.id, {
        status: targetStatus,
        note: statusNote,
        tracking_number: statusTrackingNumber,
        carrier,
        send_email: statusSendEmail,
      });
      setStatusModal({ open: false, targetStatus: '' });
      setSuccessMessage(`Order status updated to ${formatStatus(targetStatus)}`);
      setTimeout(() => setSuccessMessage(null), 5000);
      loadOrder();
    } catch (err: any) {
      console.error('Error updating status:', err);
      alert(err.response?.data?.error || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleConfirmOrder = async () => {
    if (!order) return;
    if (!window.confirm('Confirm this order? This will change the status from PENDING to CONFIRMED.')) {
      return;
    }

    try {
      setConfirmingOrder(true);
      await salesOrdersAPI.confirmOrder(order.id);
      setSuccessMessage('Order confirmed successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      loadOrder();
    } catch (err: any) {
      console.error('Error confirming order:', err);
      alert(err.response?.data?.error || 'Failed to confirm order');
    } finally {
      setConfirmingOrder(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!order) return;
    const productionStatuses = ['IN_PRODUCTION', 'READY_TO_SHIP', 'SHIPPED'];
    const isProduction = productionStatuses.includes(order.status);
    let message = `You are about to delete Order #${order.order_number}. This will also remove associated batches, allocations, and fulfillments. This cannot be undone.`;
    if (isProduction) {
      message += `\n\nWARNING: This order has active production. Deleting will affect in-progress batches.`;
    }
    message += '\n\nType DELETE to confirm:';
    const confirmation = window.prompt(message);
    if (confirmation !== 'DELETE') return;

    try {
      await salesOrdersAPI.delete(order.id);
      navigate('/orders');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete order');
    }
  };

  const handleCreateInvoiceClick = async () => {
    if (!order) return;

    const fallbackOrder: OrderNeedingInvoice = {
      id: order.id,
      order_number: order.order_number,
      customer_id: order.customer_id || 0,
      customer_name: order.customer_name,
      customer_email: order.customer_email || '',
      customer_address: order.customer_address || '',
      customer_city: order.customer_city || '',
      customer_state: order.customer_state || '',
      customer_zip: order.customer_zip || '',
      customer_tax_exempt: order.is_tax_exempt,
      order_date: order.order_date,
      delivery_date: order.delivery_date,
      total: String(order.total_amount),
      status: order.status,
      shipping_method: order.shipping_method,
      notes: order.special_instructions || '',
      items: order.items.map(item => ({
        id: item.id,
        feed_name: item.formula_name,
        quantity_lbs: item.total_quantity_lbs,
        unit_price: String(item.unit_price),
        line_total: String(item.total_price),
        package_type: item.package_type || 'BULK',
        is_pelleted: item.is_pellet,
        number_of_packages: item.number_of_packages || 1,
        price_per_package: item.price_per_package ? String(item.price_per_package) : null,
        pricing_display_method: (item.pricing_display_method as 'PER_LB' | 'PER_PACKAGE') || 'PER_LB',
      })),
    };

    try {
      const response = await getOrdersNeedingInvoice();
      const orderForInv = response.orders.find((o: OrderNeedingInvoice) => o.id === order.id);
      setOrderForInvoice(orderForInv || fallbackOrder);
    } catch (err) {
      console.error('Error getting orders for invoice:', err);
      setOrderForInvoice(fallbackOrder);
    }
  };

  if (loading) {
    return (
      <div className="order-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading order...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Order not found'}</p>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const currentStepIndex = getCurrentStepIndex();
  const isCancelled = order.status === 'CANCELLED';
  const isDraft = order.status === 'DRAFT';
  const isShippedOrDelivered = order.status === 'SHIPPED' || order.status === 'DELIVERED';

  return (
    <div className="order-detail-page">
      {/* Header */}
      <div className="order-detail-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate(-1)}>
            ← Back to Orders
          </button>
          <div className="order-title">
            <h1>Order {order.order_number}</h1>
            <span className={`status-badge large ${getStatusBadgeClass(order.status)}`}>
              {formatStatus(order.status)}
            </span>
            {order.is_rush && <span className="rush-badge">RUSH</span>}
          </div>
        </div>
        <div className="header-actions no-print">
          {/* Quick Mark Shipped button */}
          {!isShippedOrDelivered && !isCancelled && order.status !== 'DRAFT' && (
            <button
              className="btn btn-mark-shipped"
              onClick={() => openStatusModal('SHIPPED')}
            >
              Mark Shipped
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}

      {/* Main Content */}
      <div className="order-detail-content">

        {/* Status Stepper */}
        {!isCancelled && !isDraft && (
          <div className="detail-card status-stepper-card no-print">
            <div className="status-stepper">
              {STATUS_PIPELINE.map((step, index) => {
                const isCompleted = index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const isFuture = index > currentStepIndex;

                return (
                  <React.Fragment key={step.key}>
                    <div
                      className={`stepper-step ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''} ${isFuture ? 'future clickable' : ''}`}
                      onClick={() => {
                        if (isFuture) openStatusModal(step.key);
                      }}
                      title={isFuture ? `Click to move to ${step.label}` : undefined}
                    >
                      <div className="stepper-dot">
                        {isCompleted ? '\u2713' : isCurrent ? '\u25CF' : '\u25CB'}
                      </div>
                      <div className="stepper-label">{step.label}</div>
                    </div>
                    {index < STATUS_PIPELINE.length - 1 && (
                      <div className={`stepper-line ${isCompleted ? 'completed' : ''}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Tracking Info (when shipped) */}
        {isShippedOrDelivered && (order.tracking_number || order.carrier) && (
          <div className="detail-card tracking-card">
            <h2>Shipping Information</h2>
            <div className="info-grid">
              {order.carrier && (
                <div className="info-item">
                  <label>Carrier</label>
                  <span>{order.carrier}</span>
                </div>
              )}
              {order.tracking_number && (
                <div className="info-item">
                  <label>Tracking / PRO Number</label>
                  <span>{order.tracking_number}</span>
                </div>
              )}
              {order.shipped_date && (
                <div className="info-item">
                  <label>Shipped Date</label>
                  <span>{formatDate(order.shipped_date)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Order Info Section */}
        <div className="detail-card">
          <h2>Order Information</h2>
          {/* Liftgate is a physical freight-handling requirement that changes how the
              load is built and priced, so surface it as a banner — never buried in the
              grid — when required. Only shown for an explicit True. */}
          {order.liftgate_required === true && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              background: '#fef3c7', border: '1px solid #f59e0b', color: '#92400e',
              borderRadius: '0.5rem', padding: '0.75rem 1rem', margin: '0 0 1rem',
              fontWeight: 600,
            }}>
              <span aria-hidden="true">⚠️</span>
              <span>Liftgate Required — this delivery has no loading dock. Build/price freight for liftgate service.</span>
            </div>
          )}
          <div className="info-grid">
            <div className="info-item">
              <label>Customer</label>
              {order.customer_id ? (
                <Link to={`/customers?id=${order.customer_id}`} className="customer-link">
                  {order.customer_name}
                </Link>
              ) : (
                <span>{order.customer_name}</span>
              )}
            </div>
            <div className="info-item">
              <label>Order Date</label>
              <span>{formatDate(order.order_date)}</span>
            </div>
            <div className="info-item">
              <label>Delivery Date</label>
              <span>{formatDate(order.delivery_date)}</span>
            </div>
            <div className="info-item">
              <label>Shipping Method</label>
              <span>{order.shipping_method.replace(/_/g, ' ')}</span>
            </div>
            <div className="info-item">
              <label>Liftgate</label>
              {/* Tri-state: true/false are explicit choices; undefined (legacy orders
                  created before liftgate was captured) must read "Not specified" —
                  never silently rendered as "No". */}
              <span>
                {order.liftgate_required === true
                  ? <strong style={{ color: '#b45309' }}>Required</strong>
                  : order.liftgate_required === false
                    ? 'No liftgate'
                    : <span style={{ color: '#6b7280' }}>Not specified</span>}
              </span>
            </div>
            <div className="info-item">
              <label>Entered By</label>
              <span>{order.entered_by_name || '-'}</span>
            </div>
            <div className="info-item">
              <label>Tax Exempt</label>
              <span>{order.is_tax_exempt ? 'Yes' : 'No'}</span>
            </div>
            {order.customer_address && (
              <div className="info-item full-width">
                <label>Shipping Address</label>
                <span>
                  {order.customer_address}
                  {order.customer_city && `, ${order.customer_city}`}
                  {order.customer_state && `, ${order.customer_state}`}
                  {order.customer_zip && ` ${order.customer_zip}`}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Order Items Section */}
        <div className="detail-card">
          <h2>Order Items</h2>
          <div className="items-table-container">
            <table className="items-table">
              <thead>
                <tr>
                  <th>Feed Formula</th>
                  <th>Form</th>
                  <th className="text-right">Quantity (lbs)</th>
                  <th className="text-right">Packages</th>
                  <th className="text-right">Pricing</th>
                  <th className="text-right">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {order.items && order.items.length > 0 ? (
                  order.items.map((item) => (
                    <tr key={item.id} className={item.is_promo ? 'promo-item-row' : ''}>
                      <td className="formula-name">
                        {item.formula_name}
                        {item.is_promo && <span className="promo-badge">PROMO</span>}
                        {item.is_promo && item.promo_reason && (
                          <div className="promo-reason-text">{item.promo_reason}</div>
                        )}
                      </td>
                      <td>{item.is_pellet ? 'Pellet' : 'Mash'}</td>
                      <td className="text-right">{item.total_quantity_lbs.toLocaleString()}</td>
                      <td className="text-right">{item.number_of_packages}</td>
                      <td className="text-right pricing-cell">{item.is_promo ? 'Promotional' : formatItemPricing(item)}</td>
                      <td className="text-right line-total">{formatCurrency(item.total_price)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="no-data">No items</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Order Summary */}
        <div className="detail-card summary-card">
          <h2>Order Summary</h2>
          <div className="summary-grid">
            <div className="summary-row">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            <div className="summary-row">
              <span>Tax</span>
              <span>{formatCurrency(order.tax_amount)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>{formatCurrency(order.total_amount)}</span>
            </div>
          </div>

          {/* Invoice Status */}
          {order.invoice_id && (
            <div className="invoice-status">
              <span className="invoice-label">Invoice:</span>
              <Link to={`/sales/invoices/${order.invoice_id}`} className="invoice-link">
                {order.invoice_number || `#${order.invoice_id}`}
              </Link>
              {order.invoice_status && typeof order.invoice_status === 'string' && (
                <span className={`invoice-badge invoice-${order.invoice_status.toLowerCase()}`}>
                  {order.invoice_status.replace(/_/g, ' ')}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Special Instructions */}
        {order.special_instructions && (
          <div className="detail-card">
            <h2>Special Instructions</h2>
            <div className="notes-content">{order.special_instructions}</div>
          </div>
        )}

        {/* Edit History */}
        {order.edit_logs && order.edit_logs.length > 0 && (
          <div className="detail-card edit-history-card">
            <h2>Edit History</h2>
            <div className="edit-history-list">
              {order.edit_logs.map((log) => (
                <div key={log.id} className="edit-history-entry">
                  <div className="edit-history-header">
                    <span className="edit-date">
                      {new Date(log.edited_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      })}
                    </span>
                    <span className="edit-author">{log.edited_by_name}</span>
                  </div>
                  <div className="edit-reason">
                    <strong>Reason:</strong> {log.reason}
                  </div>
                  {Object.keys(log.changes).length > 0 && (
                    <div className="edit-changes">
                      <strong>Changes:</strong>{' '}
                      {Object.entries(log.changes).map(([field, change], idx) => (
                        <span key={field}>
                          {idx > 0 && ', '}
                          {field.replace(/_/g, ' ')} {String(change.old)} → {String(change.new)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons no-print">
          {order.status === 'PENDING' && (
            <button
              className="btn btn-confirm"
              onClick={handleConfirmOrder}
              disabled={confirmingOrder}
            >
              {confirmingOrder ? 'Confirming...' : 'Confirm Order'}
            </button>
          )}

          {(order.status === 'PENDING' || order.status === 'CONFIRMED' || order.status === 'DRAFT') && (
            <button
              className="btn btn-edit"
              onClick={() => navigate(`/order-entry?edit=${order.id}`)}
            >
              Edit Order
            </button>
          )}
          <button
            className="btn btn-danger"
            onClick={handleDeleteOrder}
          >
            Delete Order
          </button>

          {order.status !== 'CANCELLED' && !order.invoice_id && (
            <button
              className="btn btn-invoice"
              onClick={handleCreateInvoiceClick}
            >
              Create Invoice
            </button>
          )}

          {order.invoice_id && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate(`/sales/invoices/${order.invoice_id}`)}
            >
              View Invoice
            </button>
          )}
        </div>
      </div>

      {/* Invoice Creation Modal */}
      {orderForInvoice && (
        <InvoiceCreationModal
          order={orderForInvoice}
          onClose={() => setOrderForInvoice(null)}
          onSuccess={() => {
            setOrderForInvoice(null);
            loadOrder();
            setSuccessMessage('Invoice created successfully!');
            setTimeout(() => setSuccessMessage(null), 5000);
          }}
        />
      )}

      {/* Status Update Modal */}
      {statusModal.open && (
        <div className="status-modal-overlay" onClick={() => setStatusModal({ open: false, targetStatus: '' })}>
          <div className="status-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{statusModal.targetStatus === 'SHIPPED' ? 'Mark Order as Shipped' : 'Update Order Status'}</h2>

            <div className="status-modal-info">
              {statusModal.targetStatus === 'SHIPPED' ? (
                <p>Order: {order.order_number} — {order.customer_name}</p>
              ) : (
                <p>Move order {order.order_number} to: <strong>{formatStatus(statusModal.targetStatus)}</strong></p>
              )}
            </div>

            {/* Carrier & Tracking (shipped only) */}
            {statusModal.targetStatus === 'SHIPPED' && (
              <>
                <div className="status-modal-field">
                  <label>Carrier</label>
                  <select
                    value={statusCarrier}
                    onChange={(e) => setStatusCarrier(e.target.value)}
                  >
                    <option value="">Select carrier...</option>
                    {CARRIER_OPTIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                {statusCarrier === 'Other' && (
                  <div className="status-modal-field">
                    <label>Carrier Name</label>
                    <input
                      type="text"
                      value={statusCarrierCustom}
                      onChange={(e) => setStatusCarrierCustom(e.target.value)}
                      placeholder="Enter carrier name"
                    />
                  </div>
                )}
                <div className="status-modal-field">
                  <label>Tracking / PRO Number</label>
                  <input
                    type="text"
                    value={statusTrackingNumber}
                    onChange={(e) => setStatusTrackingNumber(e.target.value)}
                    placeholder="e.g. FW-12345678"
                  />
                </div>
              </>
            )}

            <div className="status-modal-field">
              <label>Note (optional)</label>
              <input
                type="text"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Add a note about this status change"
              />
            </div>

            <label className="status-modal-checkbox">
              <input
                type="checkbox"
                checked={statusSendEmail}
                onChange={(e) => setStatusSendEmail(e.target.checked)}
              />
              <span>
                Send notification email to customer
                {statusModal.targetStatus === 'SHIPPED' && statusTrackingNumber && (
                  <span className="checkbox-note"> (includes tracking number)</span>
                )}
              </span>
            </label>

            <div className="status-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setStatusModal({ open: false, targetStatus: '' })}
              >
                Cancel
              </button>
              <button
                className="btn btn-confirm"
                onClick={handleUpdateStatus}
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Updating...' : (
                  statusModal.targetStatus === 'SHIPPED' ? 'Mark Shipped' : 'Update Status'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
